import { usePurchaserId } from '@openeducation/hr-rn-cognito-providers';
import {
  CenteredLoading,
  HRChangePasswordModal,
  HRModal,
  HRProfile,
  useConfigurations,
  useHRPortalConfig,
  useHRPageTemplateProps,
} from '@openeducation/hr-rn-components';
import {
  ExternalPortalExperienceType,
  OrganizationType,
} from '@openeducation/hr-rn-shared-types';
import { styled } from '@openeducation/hr-rn-themes';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { useSpotfireSession } from '../hooks/use-spotfire-session';
import { useSpotfireWebClient } from '../hooks/use-spotfire-web-client';
import { Path, useNavigate } from '../router';

const SPOTFIRE_CONTAINER_ID = 'spotfire-web-client';

const ExternalPortalScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { externalPortalExperience, configurationsLoading } =
    useConfigurations();
  const { onLogout, changePasswordProps } = useHRPageTemplateProps();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangePasswordSuccess, setShowChangePasswordSuccess] =
    useState(false);
  const purchaserId = usePurchaserId();
  const config = useHRPortalConfig(OrganizationType.OPEN_ENGLISH);
  const spotfire = externalPortalExperience?.spotfire;
  const spotfireSession = useSpotfireSession(spotfire?.serverUrl, purchaserId);
  const sessionSettled =
    spotfireSession === 'ready' || spotfireSession === 'error';
  const webClient = useSpotfireWebClient(
    spotfire?.serverUrl,
    spotfire?.analysisPath,
    spotfire?.configurationBlock ?? '',
    SPOTFIRE_CONTAINER_ID,
    sessionSettled
  );
  useEffect(() => {
    if (configurationsLoading) {
      return;
    }

    if (
      externalPortalExperience?.type !== ExternalPortalExperienceType.SPOTFIRE
    ) {
      navigate(Path.Choice, { replace: true });
    }
  }, [configurationsLoading, externalPortalExperience?.type, navigate]);

  useEffect(() => {
    if (changePasswordProps.isSuccess) {
      setShowChangePasswordModal(false);
      setShowChangePasswordSuccess(true);
    }
  }, [changePasswordProps.isSuccess]);

  if (configurationsLoading) {
    return <CenteredLoading size={100} />;
  }

  // SPOTFIRE with no serverUrl is a configuration error surfaced here — not a fallback to the
  // standard portal.
  const misconfigured =
    externalPortalExperience?.type === ExternalPortalExperienceType.SPOTFIRE &&
    !spotfire?.serverUrl;
  const embedFailed =
    misconfigured || spotfireSession === 'expired' || webClient === 'error';

  return (
    <Container>
      <HeaderContainer>
        <HeaderContent>
          <View>{config?.logo}</View>
          <HRProfile
            onLogout={onLogout}
            onChangePassword={() => setShowChangePasswordModal(true)}
            iconBackgroundColor={config?.profileIconBackgroundColor}
          />
        </HeaderContent>
      </HeaderContainer>
      <FrameContainer>
        {(spotfireSession === 'loading' || webClient === 'loading') && (
          <CenteredLoading size={100} />
        )}
        {sessionSettled &&
          webClient !== 'error' &&
          React.createElement('div', {
            id: SPOTFIRE_CONTAINER_ID,
            style: embedContainerStyle,
          })}
        {embedFailed && (
          <ErrorContainer>
            <ErrorText>{t('hr2.error.unknown')}</ErrorText>
          </ErrorContainer>
        )}
      </FrameContainer>
      <HRChangePasswordModal
        {...changePasswordProps}
        visible={showChangePasswordModal}
        onCancel={() => setShowChangePasswordModal(false)}
      />
      <HRModal
        visible={showChangePasswordSuccess}
        onClose={() => setShowChangePasswordSuccess(false)}
        successMessage={{
          message: t('hr2.auth.changePassword.success'),
          backgroundColor: config?.successMessageBackgroundColor,
          color: config?.successMessageTextColor,
        }}
        autoDismiss
      />
    </Container>
  );
};

const embedContainerStyle = {
  border: 0,
  height: '100%',
  width: '100%',
};

const Container = styled.View`
  background-color: ${({ theme }) => theme.color.background};
  flex: 1;
`;

const HeaderContainer = styled.View`
  height: 80px;
  background-color: ${({ theme }) => theme.color.white};
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const HeaderContent = styled.View`
  width: 1120px;
  flex-grow: 1;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const FrameContainer = styled.View`
  flex: 1;
`;

const ErrorContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled.Text`
  font-size: 16px;
  color: ${({ theme }) => theme.color.text};
`;

export default ExternalPortalScreen;
